import { AlertCircle, Loader2, RefreshCw, Unlock, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useOpenShift, useShiftStatus } from '@/hooks/queries';
import { rupiah } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/types/api';

/**
 * The opening float and the "Buka shift" button. The float is the one finance
 * set in the back office (Finance → Cashier Float) — shown, never typed: the
 * server takes it when the shift opens. With none set a cashier cannot open
 * (the server refuses 409 CASHIER_FLOAT_NOT_SET); an account the server does
 * not require a shift of opens with Rp 0.
 */
export function OpenShiftPanel({ className }: { className?: string }) {
  const { data: status, isLoading, isFetching, refetch } = useShiftStatus();
  const openMut = useOpenShift();
  const float = status?.float ?? null;
  const canOpen = Boolean(float) || status?.required === false;

  const handleOpen = () => {
    openMut.mutate(undefined, {
      onSuccess: () => toast.success('Shift dibuka.'),
      onError: (e) => {
        const err = e as ApiError;
        toast.error(err?.message ?? 'Gagal membuka shift.');
        // Finance removed the float in the meantime: show it.
        if (err?.errorCode === 'CASHIER_FLOAT_NOT_SET') void refetch();
      },
    });
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-4 text-ink-400', className)}>
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {float ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-cream-200 bg-cream-50 px-3 py-2.5">
          <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
            <Wallet className="h-4 w-4" /> Modal awal
          </span>
          <span className="text-right">
            <span className="tnum block font-display text-lg font-semibold text-ink-900">{rupiah(float.amount)}</span>
            <span className="block text-xs text-muted-foreground">diatur Finance</span>
          </span>
        </div>
      ) : canOpen ? (
        <p className="text-sm text-muted-foreground">Modal awal: {rupiah(0)}</p>
      ) : (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Modal belum diatur</p>
            <p className="mt-0.5">Hubungi Finance untuk mengisi modal shift Anda, lalu muat ulang.</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 gap-1.5"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} /> Muat ulang
          </Button>
        </div>
      )}
      <Button type="button" className="w-full gap-2" onClick={handleOpen} disabled={!canOpen || openMut.isPending}>
        {openMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
        Buka shift
      </Button>
    </div>
  );
}
